<script setup lang="ts">
import Dialog from "primevue/dialog";
import ChevronRightIcon from "primevue/icons/chevronright";
import { computed, toValue } from "vue";
import type { RouteLocationRaw } from "vue-router";
import DeadlinePassedIcon from "@/components/shared/icons/DeadlinePassedIcon.vue";
import FlipCalendarIcon from "@/components/shared/icons/FlipCalendarIcon.vue";
import { SessionDeadlineModalPresets } from "./styles.ts";
import type { Props } from "./types";

const props = withDefaults(defineProps<Props>(), {
  blockBackground: true,
  lockToAction: true,
  title: "Deadline has passed",
  message: () => [
    "Unfortunately, this program's registration deadline has passed.",
    "Please search for another program.",
  ],
  actionLabel: "Go to results Page",
  navigateTo: () => ({ name: "results" }),
  width: "37.5rem",
});

const emit = defineEmits<(e: "update:modelValue", value: boolean) => void>();

const actionMessage = computed<string[]>(() => {
  if (props.message instanceof Array) {
    return props.message;
  }
  return [props.message];
});

const visible = computed({
  get: () => props.modelValue,
  set: (isVisible: boolean) => emit("update:modelValue", isVisible),
});

const closable = computed(() => !props.lockToAction);
const closeOnEscape = computed(() => !props.lockToAction);
const dismissableMask = computed(() => !props.lockToAction);
const navigationRedirectLocation = computed<string | RouteLocationRaw>(() => {
  return toValue(props.navigateTo);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="title"
    :showHeader="true"
    :closable="closable"
    :modal="blockBackground"
    :dismissableMask="dismissableMask"
    :closeOnEscape="closeOnEscape"
    :draggable="false"
    :resizable="false"
    :style="{ width }"
    class="ob-locked-modal"
    :pt="SessionDeadlineModalPresets.dialog"
  >
    <template #header>
      <div class="flex flex-row items-center uppercase gap-2 flex-start text-sm text-university-primary">
        <DeadlinePassedIcon />
        <span>{{ title }}</span>
      </div>
    </template>
    <div class="flex flex-col items-center justify-center text-university-primary gap-12">
      <div class="flex flex-col items-center justify-center gap-6">
          <FlipCalendarIcon />
        <h1 class="font-bold text-5xl">Uh-oh!</h1>
        <p v-if="message" class="flex flex-col mt-4 items-center justify-center text-base"><span v-for="line in actionMessage">{{ line }}<br /></span></p>
      </div>
      <div class="flex justify-center">
        <RouterLink data-testid="action-router-link" :to="navigationRedirectLocation">
          <div data-testid="action-button" class="bg-cyan-200 px-8 py-5 flex flex-row justify-between items-center gap-4">
            <span id="error-button-text" class="text-white uppercase font-bold text-base"
              >{{ actionLabel }}</span
            >
            <ChevronRightIcon class="text-white" />
          </div>
        </RouterLink>
      </div>
    </div>
  </Dialog>
</template>
